import type {
  OAuthErrorEvent,
  OAuthResponseEvent,
  PickerCanceledEvent,
  PickerPickedEvent,
} from '@googleworkspace/drive-picker-element';
import {createStyles, Menu as MantineMenu} from '@mantine/core';
import React, {lazy, Suspense, useCallback, useMemo, useState} from 'react';

import {isNodeTesting} from 'lib/Browser';
import {
  DRIVE_SCOPE,
  getGDriveOAuthClientId,
  getGDriveProjectNumber,
} from 'notebook/notes/attachments/googleDriveAuth';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {createUploadDropdownGDrivePickerHandlers} from './CreateUploadDropdownGDrivePickerHandlers';

// Used to fetch the user's Google account email after they grant OAuth consent.
const USERINFO_SCOPE = 'https://www.googleapis.com/auth/userinfo.email';

// Matches @width-medium--dropdown from less/variables.less, the legacy ui-library Menu medium width.
const DROPDOWN_MIN_WIDTH = 300;

interface DrivePickerInlineProps {
  clientId: string;
  appId: string;
  multiselect: boolean;
  scope: string;
  prompt: '' | 'none' | 'consent' | 'select_account';
  onPicked: (e: PickerPickedEvent) => void;
  onCanceled: (e: PickerCanceledEvent) => void;
  onOauthResponse: (e: OAuthResponseEvent) => void;
  onOauthError: (e: OAuthErrorEvent) => void;
}

// @googleworkspace/drive-picker-react is ESM-only (no CJS "require" export), which causes
// Node's CJS resolver to fail during Mocha test runs. We use a lazy dynamic import gated on
// isNodeTesting so the module is never resolved in tests. This follows the same pattern as
// useRolloutFlags in LaunchDarklyUtil.ts. See also: https://nodejs.org/api/errors.html#err_package_path_not_exported
const DrivePickerLazy = isNodeTesting
  ? () => null
  : lazy(() =>
      import(/* webpackChunkName: "google-drive-picker" */ '@googleworkspace/drive-picker-react').then(
        (m) => ({
          default: function DrivePickerLoaded(props: DrivePickerInlineProps): JSX.Element {
            return (
              <m.DrivePicker
                client-id={props.clientId}
                app-id={props.appId}
                multiselect={props.multiselect}
                scope={props.scope}
                prompt={props.prompt}
                onPicked={props.onPicked}
                onCanceled={props.onCanceled}
                onOauthResponse={props.onOauthResponse}
                onOauthError={props.onOauthError}
              >
                <m.DrivePickerDocsView mode="LIST" include-folders="true" select-folder-enabled="false" />
              </m.DrivePicker>
            );
          },
        })
      )
    );

interface UploadDropdownButtonProps {
  onLocalUploadClick: () => void;
  onGoogleDriveFilesReady: (files: Array<File>) => void;
  multiselect?: boolean;
  disabled?: boolean;
}

export default function UploadDropdownButton(props: UploadDropdownButtonProps): JSX.Element {
  const {onLocalUploadClick, onGoogleDriveFilesReady, multiselect = false, disabled = false} = props;

  const {classes} = useStyles();
  const [isPickerVisible, setIsPickerVisible] = useState(false);

  const hasGDriveConfig = Boolean(getGDriveOAuthClientId() && getGDriveProjectNumber());

  const handleGDriveClick = useCallback(() => {
    setIsPickerVisible(true);
  }, []);

  const {handleCanceled, handleOauthResponse, handleOauthError, handlePicked} = useMemo(
    () =>
      createUploadDropdownGDrivePickerHandlers({
        setIsPickerVisible,
        onGoogleDriveFilesReady,
      }),
    [onGoogleDriveFilesReady]
  );

  return (
    <div className={classes.dropdownContainer}>
      <MantineMenu position="bottom-start" width={DROPDOWN_MIN_WIDTH} withinPortal={false}>
        <MantineMenu.Target>
          <span>
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.SMALL}
              fullWidth={true}
              className="u-tiny-spacing-bottom"
              disabled={disabled}
              text="Upload a File"
              isDropdown={true}
              dataTestAttrs={{component: 'UploadDropdownButton', element: 'uploadButton'}}
            />
          </span>
        </MantineMenu.Target>
        <MantineMenu.Dropdown>
          <MantineMenu.Item onClick={onLocalUploadClick}>Upload from computer</MantineMenu.Item>
          {hasGDriveConfig && (
            <MantineMenu.Item onClick={handleGDriveClick}>Upload from Google Drive</MantineMenu.Item>
          )}
        </MantineMenu.Dropdown>
      </MantineMenu>
      {hasGDriveConfig && isPickerVisible && (
        <Suspense fallback={null}>
          <DrivePickerLazy
            clientId={getGDriveOAuthClientId()}
            appId={getGDriveProjectNumber()}
            multiselect={multiselect}
            scope={`${USERINFO_SCOPE} ${DRIVE_SCOPE}`}
            prompt=""
            onPicked={handlePicked}
            onCanceled={handleCanceled}
            onOauthResponse={handleOauthResponse}
            onOauthError={handleOauthError}
          />
        </Suspense>
      )}
    </div>
  );
}

const useStyles = createStyles(() => ({
  dropdownContainer: {
    maxWidth: '140px',
    width: '100%',
  },
}));
